import { Cloud, Droplets, Waves, Recycle, ShieldCheck, Users, Sprout, TrendingUp, FileCheck, Clock, Package, Landmark, Factory, Plug, Truck, Search } from "lucide-react";
import "./StageInput.css";
const PARAMETER_VISUALS = {
  gwp:[Cloud,'Climate impact'], water:[Droplets,'Water consumption'], pollution:[Waves,'Pollution burden'],
  circularity:[Recycle,'Material circularity'], risk:[ShieldCheck,'Worker health & safety'], social:[Users,'Labour practices'],
  ecosystem:[Sprout,'Ecosystem management'], economic:[TrendingUp,'Economic performance'], certifications:[FileCheck,'Certifications & ethics'],
  time:[Clock,'Activity duration'], cost:[Package,'Allocated cost'], governance:[Landmark,'Governance & compliance']
};
const STAGE_ICONS={manufacturing:Factory,use_phase:Plug,end_of_life:Recycle,logistics:Truck};
import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, ChevronRight, ChevronLeft, Loader2, CheckCircle2, Calculator, Upload, Download, Info } from "lucide-react";
import { API_BASE_URL } from "../config/api";
import ParameterCalculator from "@/components/ParameterCalculator";
import { isQualitative, QUALITATIVE_RUBRICS } from "@/lib/qualitativeRubric";
import { TOSS_LOGO_URL as LOGO_URL, BRAND } from "@/lib/branding";

import { useAuth } from "@/config/auth";
import { draftKey } from "@/lib/assessmentDrafts";
const API = API_BASE_URL;

const TRANSPARENCY_OPTIONS = [
  { value: "2", label: "Estimated" },
  { value: "5", label: "Self-reported" },
  { value: "10", label: "Verified" },
];

const PRODUCT_SUBSTAGES = [
  { key: "manufacturing_pre_processing", label: "Manufacturing - Pre-Processing" },
  { key: "manufacturing_production", label: "Manufacturing - Production" },
  { key: "use_phase_operation", label: "Use Phase - Operation" },
  { key: "use_phase_maintenance", label: "Use Phase - Maintenance & Upgrades" },
  { key: "eol_recycling", label: "End-of-Life - Recycling" },
  { key: "eol_recovery", label: "End-of-Life - Recovery" },
  { key: "logistics_transportation", label: "Logistics - Transportation" },
  { key: "logistics_storage", label: "Logistics - Storage" },
];

const bomMassKg = (items = []) => items.reduce(
  (sum, item) => sum + (Number(item.quantity ?? 1) || 0) * (Number(item.weight_kg) || 0),
  0
);

const STAGE_TABS = [
  { key: "manufacturing", label: "Manufacturing", substages: ["manufacturing_pre_processing", "manufacturing_production"] },
  { key: "use_phase", label: "Use Phase", substages: ["use_phase_operation", "use_phase_maintenance"] },
  { key: "end_of_life", label: "End-of-Life", substages: ["eol_recycling", "eol_recovery"] },
  { key: "logistics", label: "Supply Chain", substages: ["logistics_transportation", "logistics_storage"] },
];

export default function NewAssessment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requestedDraft] = useState(() => new URLSearchParams(window.location.search).get('draft'));
  const [draftId] = useState(() => requestedDraft || crypto.randomUUID());
  const [loadError,setLoadError]=useState('');
  const [ready, setReady] = useState(false);
  const [saveStatus, setSaveStatus] = useState('Loading draft…');
  const [sourceAssessmentId, setSourceAssessmentId] = useState(null);
  const [revisionOf, setRevisionOf] = useState(null);
  const [reportingScope, setReportingScope] = useState({});
  const pending = useRef(null);
  const saving = useRef(Promise.resolve());
  const completed = useRef(false);
  const version = useRef(0);
  const saveDraft = useCallback(async () => {
    const snapshot = pending.current;
    if (!snapshot || completed.current) return;
    const currentVersion = version.current;
    setSaveStatus('Saving…');
    saving.current = saving.current.catch(()=>{}).then(async () => {
      if (completed.current) return;
      await axios.put(`${API}/drafts/${draftId}`, {payload:snapshot});
      if(version.current === currentVersion) setSaveStatus('Saved');
    });
    try { await saving.current; } catch(e) { setSaveStatus('Saved on this device — retry sync'); throw e; }
  }, [draftId]);
  const [step, setStep] = useState(1);
  const [meta, setMeta] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [countries, setCountries] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const bomFileRef = useRef(null);
  const stageFileRef = useRef(null);

  const [form, setForm] = useState({
    product_name: "",
    functional_unit: "1 Kg Product",
    product_mass: 0,
    product_type: "",
    bom_items: [{ material: "", country: "", component: "", quantity: 1, weight_kg: 0 }],
  });
  const [stageInputs, setStageInputs] = useState({});
  const [stageInputUnits, setStageInputUnits] = useState({});
  const [transparency, setTransparency] = useState({});
  const [applicability, setApplicability] = useState({});
  const [calculators, setCalculators] = useState({});
  const [validationIssues, setValidationIssues] = useState([]);
  const [compactStages, setCompactStages] = useState(() => window.matchMedia('(max-width: 1199px)').matches);
  const [selectedSubstage, setSelectedSubstage] = useState({});
  useEffect(() => {
    const media = window.matchMedia('(max-width: 1199px)');
    const change = () => setCompactStages(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  const [parameterSearch,setParameterSearch]=useState({});
  const [parameterFilter,setParameterFilter]=useState({});
  const filterParameter=(ss,pk)=>{
    const query=(parameterSearch[ss] || '').toLowerCase();
    if(!`${meta?.param_labels[pk]} ${pk}`.toLowerCase().includes(query))return false;
    const filled=stageInputs[ss]?.[pk]!=null && stageInputs[ss]?.[pk]!=='';
    return !parameterFilter[ss] || parameterFilter[ss]==='all' || (parameterFilter[ss]==='empty' ? !filled : filled);
  };
  const [productBenchmarks,setProductBenchmarks]=useState({});
  const [benchmarkType,setBenchmarkType]=useState('');
  const [benchmarkError,setBenchmarkError]=useState('');
  useEffect(()=>{
    let cancelled=false;
    const loadBenchmarks=async()=>{
      setBenchmarkType('');setBenchmarkError('');
      if(!form.product_type){setProductBenchmarks({});return;}
      try {
        const {data}=await axios.get(`${API}/product-benchmarks/${form.product_type}`);
        if(!cancelled){setProductBenchmarks(data);setBenchmarkType(form.product_type);}
      } catch {if(!cancelled)setBenchmarkError('Could not load product benchmarks. Reload to retry.');}
    };
    loadBenchmarks();window.addEventListener('focus',loadBenchmarks);
    return ()=>{cancelled=true;window.removeEventListener('focus',loadBenchmarks);};
  },[form.product_type]);
  const visibleParameters = ss => benchmarkType===form.product_type ? (meta?.param_keys || []).filter(p=>productBenchmarks[ss]?.input_parameters?.[p]?.status==='applicable') : [];


  useEffect(() => {
    let cancelled=false;
    const load = async () => {
      try {
        const [metaRes, matRes, cRes, ptRes] = await Promise.all([
          axios.get(`${API}/meta`),
          axios.get(`${API}/materials`),
          axios.get(`${API}/countries`),
          axios.get(`${API}/product-types`),
        ]);
        if(cancelled) return;
        setMeta(metaRes.data);
        setMaterials(matRes.data);
        setCountries(cRes.data);
        setProductTypes(ptRes.data);

        const initInputs = {};
        const initTrans = {};
        const initUnits = {};
        for (const ss of PRODUCT_SUBSTAGES) {
          initInputs[ss.key] = {};
          initTrans[ss.key] = {};
          initUnits[ss.key] = {};
          for (const pk of metaRes.data.param_keys) {
            initInputs[ss.key][pk] = null;
            initTrans[ss.key][pk] = 5;
            initUnits[ss.key][pk] = pk === 'cost' ? 'INR/Kg product' : metaRes.data.param_units[pk];
          }
        }
        setStageInputs(initInputs);
        setTransparency(initTrans);
        setStageInputUnits(initUnits);
        const key = draftKey(user.id, draftId);
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(key)); } catch {}
        if (requestedDraft) {
          try {
            const remote = (await axios.get(`${API}/drafts/${draftId}`)).data;
            if(remote.completed_id) { completed.current=true; navigate(`/advanced/assessments/${remote.completed_id}`); return; }
            if(!saved || remote.updated_at > saved.updated_at) saved=remote;
          } catch(e) { if(!saved) throw e; }
        }
        if(cancelled) return;
        if(saved?.payload) {
          const p=saved.payload;
          setForm(p.form); setStageInputs(p.stageInputs || initInputs); setStageInputUnits(p.stageInputUnits || initUnits);
          setTransparency(p.transparency || initTrans); setApplicability(p.applicability || {}); setCalculators(p.calculators || {});
          setStep(p.step || 1); setSourceAssessmentId(p.sourceAssessmentId || null); setRevisionOf(p.revisionOf || null); setReportingScope(p.reportingScope || {});
        }
        window.history.replaceState(window.history.state, '', `${window.location.pathname}?draft=${draftId}`);
        setReady(true);
      } catch (e) {
        if(cancelled) return;
        setLoadError("Could not load the assessment. Your saved draft has not been changed. Reload to retry.");
      }
    };
    load();
    return ()=>{cancelled=true;};
  }, [draftId,navigate,user.id,requestedDraft]);

  useEffect(() => {
    if(!ready || completed.current) return;
    const payload={form,stageInputs,stageInputUnits,transparency,applicability,calculators,step,sourceAssessmentId,revisionOf,reportingScope};
    pending.current=payload; version.current++;
    try {
      localStorage.setItem(draftKey(user.id,draftId),JSON.stringify({id:draftId,payload,updated_at:new Date().toISOString()}));
      setSaveStatus('Saving…');
    } catch { setSaveStatus('Device backup unavailable — saving online…'); }
    const timer=setTimeout(()=>saveDraft().catch(()=>{}),700);
    return ()=>clearTimeout(timer);
  },[ready,form,stageInputs,stageInputUnits,transparency,applicability,calculators,step,sourceAssessmentId,revisionOf,reportingScope,draftId,user.id,saveDraft]);
  useEffect(()=>()=>{ saveDraft().catch(()=>{}); },[saveDraft]);

  const updateBOMItem = useCallback((index, field, value) => {
    setForm(prev => {
      const items = [...prev.bom_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, bom_items: items };
    });
  }, []);

  const addBOMItem = () => {
    setForm(prev => ({
      ...prev,
      bom_items: [...prev.bom_items, { material: "", country: "", component: "", quantity: 1, weight_kg: 0 }]
    }));
  };

  const removeBOMItem = (index) => {
    setForm(prev => ({ ...prev, bom_items: prev.bom_items.filter((_, i) => i !== index) }));
  };

  const updateParam = (substage, param, value) => {
    setStageInputs(prev => ({
      ...prev, [substage]: { ...prev[substage], [param]: value === "" || value == null ? null : Number(value) }
    }));
  };

  const updateParamTransparency = (substage, param, value) => {
    setTransparency(prev => ({
      ...prev, [substage]: { ...(prev[substage] || {}), [param]: parseInt(value) }
    }));
  };

  const handleBOMImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/import-assessment-excel`, fd);
      if (res.data.bom_items?.length > 0) {
        setForm(prev => ({ ...prev, bom_items: res.data.bom_items }));
        toast.success(`Imported ${res.data.bom_items.length} BOM items`);
      } else {
        toast.error("No BOM data found. Ensure sheet is named 'BOM' with columns: Material, Country, Component, Quantity, Unit_Weight_Kg");
      }
      if (res.data.errors?.length > 0) toast.error(res.data.errors[0]);
    } catch (err) {
      toast.error("Import failed: " + (err.response?.data?.detail || "Invalid file"));
    }
    if (bomFileRef.current) bomFileRef.current.value = "";
  };

  const handleStageImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/import-assessment-excel`, fd);
      const unitErrors = (res.data.errors || []).filter(msg => msg.startsWith("Missing unit:"));
      if (unitErrors.length > 0) {
        toast.error(`${unitErrors[0]}. Download the updated Stage Data template and include units.`);
        if (stageFileRef.current) stageFileRef.current.value = "";
        return;
      }
      if (Object.keys(res.data.stage_inputs || {}).length > 0) {
        setCalculators(old => {
          const next = {...old};
          for (const [ss, params] of Object.entries(res.data.stage_inputs)) {
            next[ss] = {...next[ss]};
            for (const pk of Object.keys(params)) delete next[ss][pk];
          }
          return next;
        });
        setApplicability(old => {
          const next = {...old};
          for (const [ss, params] of Object.entries(res.data.stage_inputs)) {
            next[ss] = {...next[ss]};
            for (const pk of Object.keys(params)) next[ss][pk] = {status:params[pk] == null ? 'unknown' : 'applicable'};
          }
          return next;
        });
        setStageInputs(prev => {
          const merged = { ...prev };
          for (const [ss, params] of Object.entries(res.data.stage_inputs)) {
            merged[ss] = { ...(merged[ss] || {}), ...params };
          }
          return merged;
        });
        if (Object.keys(res.data.transparency || {}).length > 0) {
          setTransparency(prev => {
            const merged = { ...prev };
            for (const [ss, params] of Object.entries(res.data.transparency)) {
              merged[ss] = { ...(merged[ss] || {}), ...params };
            }
            return merged;
          });
        }
        if (Object.keys(res.data.stage_input_units || {}).length > 0) {
          setStageInputUnits(prev => {
            const merged = { ...prev };
            for (const [ss, params] of Object.entries(res.data.stage_input_units)) {
              merged[ss] = { ...(merged[ss] || {}), ...params };
            }
            return merged;
          });
        }
        toast.success("Stage data imported successfully");
      } else {
        toast.error("No stage data found. Ensure sheet is named 'Stage Data'");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.issues?.map(i=>`${i.location}: ${i.message}`).join("; ") || "Import failed");
    }
    if (stageFileRef.current) stageFileRef.current.value = "";
  };

  const changeFunctionalUnit = (next) => {
    const mass = bomMassKg(form.bom_items);
    const scaled = {gwp:'Kg CO2-eq',water:'L',pollution:'Points',cost:'INR'};
    const nextUnits = structuredClone(stageInputUnits), nextValues = structuredClone(stageInputs);
    for (const ss of PRODUCT_SUBSTAGES) for (const [pk,numerator] of Object.entries(scaled)) {
      if (calculators[ss.key]?.[pk]) continue;
      const unit=stageInputUnits[ss.key]?.[pk] || '';
      const isKg=/\/kg/i.test(unit), isProduct=/\/(product|unit|functional unit)/i.test(unit);
      if (!isKg && !isProduct) continue;
      const value=stageInputs[ss.key]?.[pk];
      const changeBasis=isKg !== (next==='1 Kg Product');
      if (changeBasis && value != null && !(mass>0)) { toast.error('Enter finished-product weight before converting existing values.'); return; }
      nextValues[ss.key][pk]=value == null ? null : changeBasis ? (next==='1 Unit' ? value*mass : value/mass) : value;
      nextUnits[ss.key][pk]=numerator + (next==='1 Unit' ? '/product' : '/Kg product');
    }
    setStageInputs(nextValues); setStageInputUnits(nextUnits); setForm(old=>({...old,functional_unit:next}));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_mass: bomMassKg(form.bom_items),
        draft_id:draftId, source_assessment_id:sourceAssessmentId, revision_of:revisionOf,
        stage_inputs: stageInputs,
        stage_input_units: stageInputUnits,
        transparency, applicability, calculators
      };
      await saveDraft();
      const res = await axios.post(`${API}/assessments`, payload);
      completed.current=true;
      localStorage.removeItem(draftKey(user.id,draftId));
      toast.success(`TOSS Score: ${(res.data.results.upsi * 10).toFixed(2)}`);
      navigate(`/advanced/assessments/${res.data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setValidationIssues(detail?.issues || []);
      toast.error(typeof detail === "string" ? detail : detail?.message || "Calculation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if(loadError) return <div className="p-8" role="alert">{loadError}<Button onClick={()=>window.location.reload()}>Retry</Button></div>;
  if (!meta || !ready) {
    return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;
  }

  const canProceedStep1 = bomMassKg(form.bom_items) > 0 && form.product_name && form.product_type && form.bom_items.every(i => i.material && i.country && i.weight_kg > 0 && Number(i.quantity) > 0);
  const wizardSteps = ["BOM Setup", "Data Entry & Transparency", "Review & Calculate"];

  return (
    <div className={step === 2 ? "p-4 md:p-8 comparison-page" : "p-8 max-w-5xl"} data-testid="new-assessment">
      <div className="flex items-center gap-3 mb-2">
        <img src={LOGO_URL} alt={BRAND.short} className="h-8 w-auto object-contain" />
        <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{revisionOf ? "Revise Assessment" : "Assessment Draft"}</h1>
      </div>
      <p className="text-sm text-[#617364] mb-6">TOSS - Calculate sustainability score for your product</p>

      <div className="flex items-center gap-4 mb-5"><span role="status" aria-live="polite">{saveStatus}</span><Button variant="outline" onClick={()=>saveDraft().catch(()=>{})}>Save draft</Button><Button variant="ghost" onClick={()=>navigate('/advanced/assessments')}>All assessments & drafts</Button></div>
      {revisionOf && <p className="text-sm mb-4">Revising assessment {revisionOf}. The original report is retained.</p>}
      <input type="file" ref={bomFileRef} accept=".xlsx,.xls" onChange={handleBOMImport} className="hidden" data-testid="bom-file-input" />
      <input type="file" ref={stageFileRef} accept=".xlsx,.xls" onChange={handleStageImport} className="hidden" data-testid="stage-file-input" />

      {/* Step Indicator */}
      <div className="wizard-step-indicator mb-8 border-b border-[#E0E6E1]">
        {wizardSteps.map((label, i) => (
          <button key={i}
            className={`wizard-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "completed" : ""}`}
            onClick={() => { if (i + 1 < step) setStep(i + 1); }}
            data-testid={`wizard-step-${i + 1}`}>
            <span className="step-num">{step > i + 1 ? <CheckCircle2 className="w-3 h-3" /> : i + 1}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* STEP 1: BOM Setup */}
      {step === 1 && (
        <div className="space-y-6" data-testid="step-1-bom">
          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Product Details</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(`${API}/template/bom`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="download-bom-template">
                    <Download className="w-3.5 h-3.5 mr-1" /> Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => bomFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="import-bom-btn">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Import Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Name</Label>
                  <Input value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))}
                    placeholder="e.g. 500 mL Glass Bottle" className="mt-1 border-[#E0E6E1] focus:ring-[#43A047]/30 focus:border-[#43A047]" data-testid="product-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Type</Label>
                  <Select value={form.product_type} onValueChange={v => setForm(p => ({ ...p, product_type: v }))}>
                    <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="product-type-select"><SelectValue placeholder="Select product type" /></SelectTrigger>
                    <SelectContent>{productTypes.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Reporting functional unit</Label>
                  <select aria-label="Functional unit" value={form.functional_unit} onChange={e => changeFunctionalUnit(e.target.value)} className="mt-1 border rounded p-2 w-full" data-testid="functional-unit-input"><option value="1 Kg Product">Per 1 kg of product</option><option value="1 Unit">Per 1 product unit</option></select>
                  <p className="text-xs mt-2">Sets the basis for physical totals in the report. Changing it also converts manually entered GWP, water, pollution and cost. Always follow the Input unit shown beside each field; imported or previously calculated values may retain a different basis.</p>
                  <p className="mt-3 font-medium">Product weight: {bomMassKg(form.bom_items).toFixed(4)} kg</p>
                  <p className="text-xs mt-2">Calculated from the quantities × weights in your BOM. Enter materials for one complete product, including its packaging.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Bill of Materials</CardTitle>
                <Button onClick={addBOMItem} variant="outline" size="sm" className="border-[#E0E6E1] text-[#43A047]" data-testid="add-bom-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Material
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E0E6E1]">
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Material</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Country</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Component</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Qty</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Unit Weight (Kg)</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.bom_items.map((item, idx) => (
                    <TableRow key={idx} className="border-[#E0E6E1]">
                      <TableCell>
                        <Select value={item.material} onValueChange={v => updateBOMItem(idx, "material", v)}>
                          <SelectTrigger className="border-[#E0E6E1] w-[140px]" data-testid={`bom-material-${idx}`}><SelectValue placeholder="Material" /></SelectTrigger>
                          <SelectContent>{materials.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={item.country} onValueChange={v => updateBOMItem(idx, "country", v)}>
                          <SelectTrigger className="border-[#E0E6E1] w-[120px]" data-testid={`bom-country-${idx}`}><SelectValue placeholder="Country" /></SelectTrigger>
                          <SelectContent>{countries.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input value={item.component} onChange={e => updateBOMItem(idx, "component", e.target.value)} placeholder="Component" className="border-[#E0E6E1] w-[160px]" data-testid={`bom-component-${idx}`} /></TableCell>
                      <TableCell><Input type="number" min="1" value={item.quantity} onChange={e => updateBOMItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="border-[#E0E6E1] w-[70px]" data-testid={`bom-qty-${idx}`} /></TableCell>
                      <TableCell><Input type="number" min="0" step="0.001" value={item.weight_kg} onChange={e => updateBOMItem(idx, "weight_kg", parseFloat(e.target.value) || 0)} className="border-[#E0E6E1] w-[100px]" data-testid={`bom-weight-${idx}`} /></TableCell>
                      <TableCell>{form.bom_items.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeBOMItem(idx)} className="text-[#D32F2F]" data-testid={`bom-remove-${idx}`}><Trash2 className="w-4 h-4" /></Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-3 text-sm text-[#617364]">Total Product Mass (Σ quantity × unit weight): <span className="font-medium text-[#1B291D]">{bomMassKg(form.bom_items).toFixed(4)} Kg</span></div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="next-step-1">
              Next: Data Entry & Transparency <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2: Data Entry with inline Transparency (NO material-based transparency) */}
      {step === 2 && (
        <div className="stage-entry space-y-6" data-testid="step-2-data-entry">
          <div className="bg-white border border-[#E0E6E1] rounded-lg p-4 mb-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#617364]">
                <span className="font-medium text-[#1B291D]">Material-based stages</span> (Extraction, Processing, Disposal) use the selected country datasets and their evidence levels. Unclassified data is estimated (2/10).
                Enter values and <span className="font-medium text-[#43A047]">transparency per parameter</span> for product-based stages below.
              </p>
              <div className="flex gap-2 ml-4 shrink-0">
                <Button variant="outline" size="sm" onClick={() => window.open(`${API}/template/stage-data`, '_blank')} className="border-[#E0E6E1] text-[#617364]" data-testid="download-stage-template">
                  <Download className="w-3.5 h-3.5 mr-1" /> Template
                </Button>
                <Button variant="outline" size="sm" onClick={() => stageFileRef.current?.click()} className="border-[#E0E6E1] text-[#43A047]" data-testid="import-stage-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Import
                </Button>
              </div>
            </div>
          </div>

          <p className="text-sm text-[#617364]">Inputs follow the selected product's benchmarks. Blank or Not applicable benchmark parameters are omitted; rubric parameters remain unless marked Not applicable. Leaving an applicable input blank automatically assigns its least-favourable score (0/100), recorded as an assumption.</p>
          {benchmarkError && <p role="alert" className="text-red-700">{benchmarkError}</p>}
          {benchmarkType!==form.product_type && !benchmarkError && <p role="status">Loading applicable parameters…</p>}
          <Tabs defaultValue="manufacturing" className="w-full">
            <TabsList className="bg-[#F8F9F8] border border-[#E0E6E1] p-1 mb-4">
              {STAGE_TABS.map(st => (
                <TabsTrigger key={st.key} value={st.key} className="data-[state=active]:bg-white data-[state=active]:text-[#43A047] text-sm" data-testid={`tab-${st.key}`}>
                  {(() => {const Icon=STAGE_ICONS[st.key];return <Icon size={18} className="mr-2"/>;})()}{st.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {STAGE_TABS.map(st => {
              const shown = compactStages ? [selectedSubstage[st.key] || st.substages[0]] : st.substages;
              const searchKey = st.substages[0];
              const parameters = Object.keys(PARAMETER_VISUALS).filter(pk => shown.some(key => visibleParameters(key).includes(pk)) && shown.some(key => visibleParameters(key).includes(pk) && filterParameter(key, pk)));
              const Icon = STAGE_ICONS[st.key];
              return <TabsContent key={st.key} value={st.key}>
                <Card className="stage-input-card stage-comparison">
                  <CardHeader className="stage-card-heading"><div className="stage-card-title"><span className="stage-heading-icon"><Icon size={24}/></span><div><CardTitle>{st.label}</CardTitle><p>Enter the applicable values and transparency for each substage.</p></div></div></CardHeader>
                  <CardContent>
                    {compactStages && <div className="substage-switch" role="group" aria-label={`${st.label} substage`}>
                      {st.substages.map(key => <button type="button" key={key} aria-pressed={shown[0] === key} onClick={() => setSelectedSubstage(old => ({...old, [st.key]:key}))}>{PRODUCT_SUBSTAGES.find(ss => ss.key === key).label}</button>)}
                    </div>}
                    <div className="parameter-toolbar">
                      <label className="parameter-search"><Search size={17}/><input aria-label={`Search ${st.label} parameters`} placeholder="Search parameters…" value={parameterSearch[searchKey] || ''} onChange={e=>setParameterSearch(old=>({...old,...Object.fromEntries(st.substages.map(key=>[key,e.target.value]))}))}/></label>
                      <label className="parameter-filter">Show: <select aria-label={`Filter ${st.label} parameters`} value={parameterFilter[searchKey] || 'all'} onChange={e=>setParameterFilter(old=>({...old,...Object.fromEntries(st.substages.map(key=>[key,e.target.value]))}))}><option value="all">All parameters</option><option value="empty">Missing values</option><option value="filled">Entered values</option></select></label>
                    </div>
                    <div className="comparison-scroll"><Table>
                      <TableHeader>
                        <TableRow><TableHead rowSpan={2} className="parameter-name-cell">Parameter</TableHead><TableHead rowSpan={2} className="comparison-unit">Input unit</TableHead>{shown.map(key=><TableHead key={key} colSpan={2} className="substage-heading">{PRODUCT_SUBSTAGES.find(ss=>ss.key===key).label}</TableHead>)}</TableRow>
                        <TableRow>{shown.map(key=><Fragment key={key}><TableHead className="substage-start">Value</TableHead><TableHead>Transparency</TableHead></Fragment>)}</TableRow>
                      </TableHeader>
                      <TableBody>{parameters.map(pk => {
                        const qual = isQualitative(pk);
                        const ParamIcon = PARAMETER_VISUALS[pk][0];
                        const units = [...new Set(shown.filter(key=>visibleParameters(key).includes(pk)).map(key=>qual ? 'Score 0–10' : stageInputUnits[key]?.[pk] || meta.param_units[pk]))];
                        return <TableRow key={pk}>
                          <TableCell className="parameter-name-cell"><div className="parameter-identity"><span className={`parameter-icon ${pk==='water'||pk==='pollution'?'parameter-icon-blue':''}`}><ParamIcon size={20}/></span><div className="parameter-copy"><strong>{meta.param_labels[pk]}</strong><small>{PARAMETER_VISUALS[pk][1]}</small></div><ParameterCalculator parameter={pk} savedCalculations={shown.filter(key=>calculators[key]?.[pk]).map(key=>({label:PRODUCT_SUBSTAGES.find(ss=>ss.key===key).label,onRemove:()=>setCalculators(old=>{const next={...old,[key]:{...old[key]}};delete next[key][pk];return next;})}))}/></div></TableCell>
                          <TableCell className="comparison-unit"><div className="parameter-unit">{units.length===1 ? units[0] : 'See each value'}</div></TableCell>
                          {shown.map(ssKey=>{
                            const ss=PRODUCT_SUBSTAGES.find(item=>item.key===ssKey);
                            if(!visibleParameters(ssKey).includes(pk))return <TableCell className="substage-start not-applicable" colSpan={2} key={ssKey}>Not applicable</TableCell>;
                            return <Fragment key={ssKey}><TableCell className="substage-start value-cell">
                                    {qual ? <Select disabled={Boolean(calculators[ssKey]?.[pk])} value={stageInputs[ssKey]?.[pk] === undefined || stageInputs[ssKey]?.[pk] === null || stageInputs[ssKey]?.[pk] === '' ? '' : String(stageInputs[ssKey][pk])} onValueChange={v => updateParam(ssKey, pk, v)}>
                                      <SelectTrigger className="text-xs min-h-8 h-auto text-left [&>span]:whitespace-normal" aria-label={`${ss.label} ${meta.param_labels[pk]} value`} data-testid={`input-${ssKey}-${pk}`}><SelectValue placeholder="Select rubric level" /></SelectTrigger>
                                      <SelectContent className="max-w-[440px]">
                                        {stageInputs[ssKey]?.[pk] !== undefined && stageInputs[ssKey]?.[pk] !== null && stageInputs[ssKey]?.[pk] !== '' && !QUALITATIVE_RUBRICS[pk].options.some(o => o.value === Number(stageInputs[ssKey][pk])) && <SelectItem value={String(stageInputs[ssKey][pk])}>Existing score: {stageInputs[ssKey][pk]} / 10</SelectItem>}
                                        {QUALITATIVE_RUBRICS[pk].options.map(o => <SelectItem key={o.value} value={String(o.value)} className="text-xs">{o.value} — {o.label}</SelectItem>)}
                                      </SelectContent>
                                    </Select> : <Input type="number" min="0" max={pk === 'circularity' ? 100 : undefined} step="any" disabled={Boolean(calculators[ssKey]?.[pk])} value={stageInputs[ssKey]?.[pk] ?? ''} onChange={e => updateParam(ssKey, pk, e.target.value)} aria-label={`${ss.label} ${meta.param_labels[pk]} value`} placeholder="Enter value" className="text-sm h-8" data-testid={`input-${ssKey}-${pk}`} />}
                              {units.length>1 && <small className="text-slate-500">{stageInputUnits[ssKey]?.[pk] || meta.param_units[pk]}</small>}
                            </TableCell><TableCell className="transparency-cell">
                                  <Select value={String(transparency[ssKey]?.[pk] || 5)} onValueChange={v => updateParamTransparency(ssKey, pk, v)}>
                                    <SelectTrigger className="h-8 text-xs border-[#E0E6E1]" data-testid={`trans-${ssKey}-${pk}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {TRANSPARENCY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs"><span className={`evidence-dot evidence-${o.value}`}/>{o.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                            </TableCell></Fragment>;
                          })}
                        </TableRow>;
                      })}
                      {parameters.length===0 && <TableRow><TableCell colSpan={2+shown.length*2} className="text-center py-8 text-slate-500">No matching applicable parameters.</TableCell></TableRow>}
                      </TableBody>
                    </Table></div>
                  </CardContent>
                </Card>
              </TabsContent>;
            })}
          </Tabs>
          <div className="stage-help"><div><strong>Need help with values?</strong><p>Use a template or view benchmarks. Formula references are available beside each parameter name.</p></div><Button variant="outline" onClick={()=>window.open('/advanced/benchmarks','_blank','noopener')}>View benchmarks</Button><Button variant="outline" onClick={()=>stageFileRef.current?.click()}>Import from template</Button></div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#E0E6E1]" data-testid="prev-step-2"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={() => setStep(3)} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="next-step-2">Next: Review & Calculate <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* STEP 3: Review & Calculate */}
      {step === 3 && (
        <div className="space-y-6" data-testid="step-3-review"><p className="text-sm text-green-900">This assessment uses Process B: 50% mean performance + 50% ESGE shortfall component, applied at stage and product levels. Earlier saved reports keep their original method.</p>
          {validationIssues.length > 0 && <div role="alert" className="bg-red-50 border border-red-200 rounded p-4"><b>Resolve these inputs before calculation</b><ul className="text-sm mt-2 space-y-1">{validationIssues.map((i,n) => <li key={n}>{i.location}: {i.message}</li>)}</ul></div>}
          <Card className="border-[#E0E6E1] bg-white">
            <CardHeader><CardTitle className="text-lg font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Review Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product</p><p className="text-sm text-[#1B291D] font-medium">{form.product_name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Type</p><p className="text-sm text-[#1B291D] font-medium">{productTypes.find(pt => pt.id === form.product_type)?.name}</p></div>
                <div><p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product Mass</p><p className="text-sm text-[#1B291D] font-medium">{bomMassKg(form.bom_items).toFixed(4)} Kg</p></div>
              </div>
              <div className="border-t border-[#E0E6E1] pt-4">
                <p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold mb-2">BOM Materials</p>
                {form.bom_items.map((item, i) => (
                  <div key={i} className="flex items-center gap-4 text-sm text-[#1B291D] py-1">
                    <Badge variant="outline" className="border-[#81C784] text-[#43A047]">{item.material}</Badge>
                    <span className="text-[#617364]">{item.country}</span><span>{item.component}</span>
                    <span className="text-[#617364]">{item.quantity || 1} × {item.weight_kg} Kg = {((Number(item.quantity ?? 1) || 0) * (Number(item.weight_kg) || 0)).toFixed(4)} Kg</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E0E6E1] pt-4">
                <p className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold mb-2">Benchmark Routes</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
                    <p className="text-sm font-medium text-[#43A047]">Material-Based (Step 3A)</p>
                    <p className="text-xs text-[#617364] mt-1">Extraction, Processing, Disposal — selected country datasets; recorded evidence levels apply. Unclassified data is estimated (2/10).</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#F8F9F8] border border-[#E0E6E1]">
                    <p className="text-sm font-medium text-[#43A047]">Product-Based (Step 3B)</p>
                    <p className="text-xs text-[#617364] mt-1">Best/Worst for {productTypes.find(pt => pt.id === form.product_type)?.name}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="border-[#E0E6E1]" data-testid="prev-step-3"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
            <Button onClick={handleSubmit} disabled={submitting || benchmarkType!==form.product_type} className="bg-[#43A047] hover:bg-[#388E3C] text-white px-8" data-testid="calculate-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Calculating...</> : <><Calculator className="w-4 h-4 mr-2" /> Calculate PSI</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
